import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api from "../lib/api";
import { SareeCard } from "../components/SareeCard";
import { inr } from "../lib/format";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { Slider } from "../components/ui/slider";

const OCCASIONS = ["All", "Bridal", "Festive", "Cocktail", "Casual"];
const SORTS = [
  { v: "featured", l: "Featured" },
  { v: "price_asc", l: "Price: Low to High" },
  { v: "price_desc", l: "Price: High to Low" },
];

export default function Catalog() {
  const [params, setParams] = useSearchParams();
  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [maxPrice, setMaxPrice] = useState(50000);
  const [sort, setSort] = useState("featured");

  const category = params.get("category") || "All";
  const occasion = params.get("occasion") || "All";
  const collection = params.get("collection") || "All";

  useEffect(() => {
    api.get("/products/categories").then((r) => setCategories(["All", ...r.data])).catch(() => {});
  }, []);

  useEffect(() => {
    const q = {};
    if (category !== "All") q.category = category;
    if (occasion !== "All") q.occasion = occasion;
    if (collection !== "All") q.collection = collection;
    q.max_price = maxPrice;
    if (sort !== "featured") q.sort = sort;
    api.get("/products", { params: q }).then((r) => setProducts(r.data)).catch(() => {});
  }, [category, occasion, collection, maxPrice, sort]);

  const setParam = (key, val) => {
    const next = new URLSearchParams(params);
    if (val === "All") next.delete(key);
    else next.set(key, val);
    setParams(next);
  };

  return (
    <div className="max-w-[1400px] mx-auto px-4 sm:px-8 lg:px-12 py-12" data-testid="catalog-page">
      <div className="mb-10">
        <span className="caption">The atelier</span>
        <h1 className="font-serif text-4xl sm:text-5xl text-gold-light mt-2">The Collection</h1>
        <p className="text-muted-foreground mt-2">Every drape can be previewed on you in the Virtual Try-On studio.</p>
        {collection !== "All" && (
          <button
            onClick={() => setParam("collection", "All")}
            data-testid="active-collection-badge"
            className="inline-flex items-center gap-2 mt-4 text-sm bg-gold/15 text-gold px-4 py-1.5 rounded-full hover:bg-gold/25 transition-colors"
          >
            The {collection} Edit · clear ✕
          </button>
        )}
      </div>

      <div className="grid lg:grid-cols-[260px_1fr] gap-10">
        {/* Filters */}
        <aside className="space-y-8 lg:sticky lg:top-24 self-start" data-testid="filters">
          <div>
            <p className="caption mb-3">Fabric</p>
            <div className="flex flex-wrap gap-2">
              {categories.map((c) => (
                <button
                  key={c}
                  onClick={() => setParam("category", c)}
                  data-testid={`filter-category-${c.toLowerCase().replace(/\s/g, "-")}`}
                  className={`text-sm px-3 py-1.5 rounded-full border transition-colors ${category === c ? "bg-gold text-obsidian border-gold" : "border-gold/25 text-gold-muted hover:border-gold"}`}
                >
                  {c}
                </button>
              ))}
            </div>
          </div>

          <div>
            <p className="caption mb-3">Occasion</p>
            <div className="flex flex-wrap gap-2">
              {OCCASIONS.map((o) => (
                <button
                  key={o}
                  onClick={() => setParam("occasion", o)}
                  data-testid={`filter-occasion-${o.toLowerCase()}`}
                  className={`text-sm px-3 py-1.5 rounded-full border transition-colors ${occasion === o ? "bg-gold text-obsidian border-gold" : "border-gold/25 text-gold-muted hover:border-gold"}`}
                >
                  {o}
                </button>
              ))}
            </div>
          </div>

          <div>
            <p className="caption mb-3">Max price · {inr(maxPrice)}</p>
            <Slider
              value={[maxPrice]}
              min={2000}
              max={50000}
              step={1000}
              onValueChange={(v) => setMaxPrice(v[0])}
              data-testid="filter-price-slider"
            />
          </div>
        </aside>

        {/* Grid */}
        <div>
          <div className="flex items-center justify-between mb-6">
            <p className="text-sm text-muted-foreground" data-testid="result-count">{products.length} sarees</p>
            <Select value={sort} onValueChange={setSort}>
              <SelectTrigger className="w-[200px] bg-obsidian-2 border-gold/25" data-testid="sort-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-obsidian-2 border-gold/25">
                {SORTS.map((s) => <SelectItem key={s.v} value={s.v}>{s.l}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          {products.length === 0 ? (
            <p className="text-muted-foreground py-20 text-center">No sarees match your filters.</p>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-6">
              {products.map((p, i) => <SareeCard key={p.id} product={p} index={i} />)}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
